<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CTextarea from '@/components/shared/CTextarea.vue';
import type { BoundedTextElement, TextPathCandidate } from '@/typings/customilyElements/canvasElements';

const MAX_FILE_MB = 2;

const props = defineProps<{
    value: boolean;
    textElement?: BoundedTextElement;
}>();

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'apply', pathData: string): void;
}>();

const source = ref('');
const fileError = ref('');
const selectedIndex = ref(0);
const isDraggingOver = ref(false);
const fileInput = ref<HTMLInputElement>();

const candidates = computed<TextPathCandidate[]>(() => props.textElement?.parseTextPathSource(source.value) ?? []);
const selected = computed(() => candidates.value[selectedIndex.value]);
const errorMsg = computed(() => {
    if (fileError.value) return fileError.value;
    return source.value.trim() && !candidates.value.length ? 'No usable path was found in this SVG.' : '';
});

watch(candidates, () => {
    selectedIndex.value = 0;
});

watch(
    () => props.value,
    (isOpen) => {
        if (!isOpen) return;
        source.value = '';
        fileError.value = '';
    }
);

const onSourceInput = (value: string) => {
    source.value = value;
    fileError.value = '';
};

const readFile = async (file?: File) => {
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
        fileError.value = `The file is larger than ${MAX_FILE_MB} MB.`;
        return;
    }
    source.value = await file.text();
    fileError.value = '';
};

const openFilePicker = () => fileInput.value?.click();

const onFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement;
    readFile(input.files?.[0]);
    // Otherwise picking the same file again doesn't fire change
    input.value = '';
};

const onDrop = (event: DragEvent) => {
    isDraggingOver.value = false;
    readFile(event.dataTransfer?.files[0]);
};

const close = () => emit('input', false);

const apply = () => {
    if (!selected.value) return;
    emit('apply', selected.value.pathData);
    close();
};
</script>

<template>
    <c-modal :value="value" title="Import Text Path" hide-footer @input="emit('input', $event)" @closed="close">
        <div
            class="drop-zone"
            :class="{ 'drag-over': isDraggingOver }"
            @dragover.prevent="isDraggingOver = true"
            @dragleave.prevent="isDraggingOver = false"
            @drop.prevent="onDrop"
        >
            <span>Drop an SVG file here or</span>
            <c-button text="Choose file" variant="secondary" size="sm" @click="openFilePicker" />
            <input ref="fileInput" type="file" accept=".svg,image/svg+xml" hidden @change="onFileSelected" />
        </div>

        <c-textarea
            class="mt-3"
            :value="source"
            :rows="4"
            :debounce="0"
            label="Or paste SVG code"
            placeholder="<svg>…</svg> or a path like M 62 174 L 104 66 C 362 140 362 180 320 182"
            :error="!!errorMsg"
            :error-msg="errorMsg"
            @input="onSourceInput"
        />

        <template v-if="candidates.length">
            <label class="mt-3 mb-1">
                {{ candidates.length > 1 ? 'Choose the path the text will follow' : 'Preview' }}
            </label>
            <div class="candidates">
                <button
                    v-for="(candidate, index) in candidates"
                    :key="index"
                    type="button"
                    class="candidate"
                    :class="{ selected: index === selectedIndex }"
                    @click="selectedIndex = index"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" :viewBox="candidate.viewBox" fill="none">
                        <path :d="candidate.pathData" vector-effect="non-scaling-stroke" />
                        <circle :cx="candidate.start.x" :cy="candidate.start.y" :r="candidate.size * 0.04" />
                    </svg>
                </button>
            </div>
            <small class="text-muted">The dot marks where the text starts.</small>
        </template>

        <div class="d-flex justify-content-between custom-footer">
            <c-button text="Cancel" variant="secondary" @click="close" />
            <c-button text="Apply" variant="primary" :disabled="!selected" @click="apply" />
        </div>
    </c-modal>
</template>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.drop-zone {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border: 1px dashed get-color('gray-light');
    border-radius: 0.25rem;
    padding: 1.25rem 1rem;

    &.drag-over {
        background-color: get-color('gray-lightest');
    }
}

.candidates {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 0.5rem;
}

.candidate {
    aspect-ratio: 1;
    border: 1px solid get-color('gray-light');
    border-radius: 0.25rem;
    background: #ffffff;
    padding: 0.5rem;

    &.selected {
        border-color: #5181ff;
        box-shadow: 0 0 0 1px #5181ff;
    }

    svg {
        width: 100%;
        height: 100%;
    }

    path {
        stroke: #333333;
        stroke-width: 2;
    }

    circle {
        fill: #5181ff;
    }
}

.custom-footer {
    border-top: 1px solid get-color('gray-light');
    margin: 1rem -1rem 0;
    padding: 1rem 1rem 0;
}
</style>
